import { TranslationService } from "../../../services/TranslationService";
import { formatCurrencyPortal } from "../../../utils/FormatUtils";

type PaymentSummaryProps = {
  selectedCount: number;
  totalAmount: number;
  currencySymbol: string;
  onPayClick: () => void;
  showCents: boolean;
  hasMultipleCurrencies: boolean;
  canPay: boolean;
  canManage: boolean;
  hasNonPayableInvoices?: boolean;
  nonPayableInvoices?: { id: number; nro: string }[];
  allowsMultipleInvoices?: boolean;
};

export const PaymentSummary = ({
  selectedCount,
  totalAmount,
  currencySymbol,
  onPayClick,
  showCents,
  hasMultipleCurrencies,
  canPay,
  canManage,
  hasNonPayableInvoices = false,
  nonPayableInvoices = [],
  allowsMultipleInvoices = true
}: PaymentSummaryProps) => {
  const hasSelection = selectedCount > 0;

  // Verificar si se permite pago múltiple y hay más de una factura seleccionada
  const multipleInvoicesNotAllowed =
    !allowsMultipleInvoices && selectedCount > 1;
  const isTotalZero = totalAmount === 0;
  const hasErrors =
    canPay &&
    hasSelection &&
    (hasMultipleCurrencies ||
      hasNonPayableInvoices ||
      multipleInvoicesNotAllowed ||
      isTotalZero);
  const errorMessages: string[] = [];

  if (hasErrors) {
    if (hasMultipleCurrencies) {
      errorMessages.push(
        "Las facturas seleccionadas tienen diferentes monedas"
      );
    }

    if (hasNonPayableInvoices && nonPayableInvoices.length > 0) {
      const invoiceNumbers = nonPayableInvoices
        .map((inv) => inv.nro || inv.id)
        .join(", ");
      errorMessages.push(
        `La(s) factura(s) ${invoiceNumbers} no están disponibles para realizar el pago`
      );
    }

    if (multipleInvoicesNotAllowed) {
      errorMessages.push(
        "Solo se puede pagar una factura a la vez. Puede seleccionar múltiples facturas para enviar mensajes."
      );
    }

    if (isTotalZero) {
      errorMessages.push("El monto total a pagar es cero");
    }
  }

  const legend = canPay
    ? canManage
      ? TranslationService.translate.CustomerPortalSelectInvoicesLegend
      : TranslationService.translate.CustomerPortalSelectInvoicesLegendPayment
    : TranslationService.translate.CustomerPortalSelectInvoicesLegendManagement;

  return (
    <div className={`payment-summary-bar${hasSelection ? "" : " is-empty"}`}>
      <div className="payment-summary-content">
        <div className="payment-summary-info">
          {hasSelection ? (
            <span className="payment-summary-text">
              {selectedCount}{" "}
              {selectedCount === 1
                ? "Factura seleccionada"
                : "Facturas seleccionadas"}{" "}
              {TranslationService.translate.By}{" "}
              {formatCurrencyPortal(totalAmount, currencySymbol, showCents)}
            </span>
          ) : (
            <span className="payment-summary-legend">
              <i className="fa-light fa-circle-info me-2"></i>
              {legend}
            </span>
          )}
          {hasErrors &&
            errorMessages.map((message, index) => (
              <span key={index} className="payment-summary-error">
                <i className="fa-light fa-exclamation-triangle me-2"></i>
                {message}
              </span>
            ))}
        </div>
        {canPay && (
          <button
            className="btn btn-payment"
            onClick={onPayClick}
            disabled={!hasSelection || hasErrors}
            title={
              !hasSelection
                ? legend
                : isTotalZero
                  ? "El monto total a pagar es cero"
                  : multipleInvoicesNotAllowed
                    ? "Solo se puede pagar una factura a la vez"
                    : hasMultipleCurrencies
                      ? "No se pueden pagar facturas con diferentes monedas"
                      : hasNonPayableInvoices
                        ? "Algunas facturas no están disponibles para realizar el pago"
                        : "Pagar facturas seleccionadas"
            }
          >
            Pagar
          </button>
        )}
      </div>
    </div>
  );
};
